import { useState } from 'react'
import { faAngleDoubleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleDoubleLeft'
import { faAngleDoubleRight } from '@fortawesome/pro-regular-svg-icons/faAngleDoubleRight'
import styled from 'styled-components'

import { Box, Flex, Icon, IconButton, Modal, Text, Tooltip } from 'components'
import { Footer } from './Footer'
import { MasterLeadBox } from './MasterLeadBox'

import { MergeLeadData } from '../../types'
import { colors } from 'utils/colors'
import { MergeLeads } from 'services/types/lead'

type Props = {
  mergeLeadData: MergeLeadData
  onClose: VoidFunction
  onSubmit: (payload: MergeLeads.Payload) => Promise<void>
  isSubmitting?: boolean
}

export const MergeConfirmationModal = ({ mergeLeadData, onClose, onSubmit, isSubmitting }: Props) => {
  const [mergePayload, setMergePayload] = useState<MergeLeads.Payload | null>(null)

  const handleChange = ({ masterLeadId, mergingLeadId }: { masterLeadId: string; mergingLeadId: string }) => {
    setMergePayload({
      master_lead_id: masterLeadId,
      merged_ids: [mergingLeadId],
    })
  }

  const handleSubmit = () => {
    if (mergePayload) {
      onSubmit(mergePayload)
      return
    }

    console.error('mergePayload is empty')
  }

  return (
    <Modal
      visible
      onClose={onClose}
      minHeight="100%"
      styleOverrides={{
        modalContent: {
          padding: '0px',
        },
      }}
      minWidth="auto"
      customSize={{
        maxWidth: 'auto',
        maxHeight: 'auto',
      }}
      footer={
        <Footer
          isSubmitDisabled={mergePayload == null}
          isSubmitting={isSubmitting}
          onCancel={onClose}
          onSubmit={handleSubmit}
        />
      }
    >
      <Flex
        padding={'24px 24px 16px'}
        borderBottom={`1px solid ${colors.gray3}`}
        width={'100%'}
        justifyContent={'space-between'}
      >
        <Flex flexDirection={'column'} gap={'10px'} alignItems={'flex-start'}>
          <Text fontSize={'16px'} fontWeight="medium">
            merge_leads_preview_lbl
          </Text>
          <Flex gap="5px">
            <Text fontSize={'14px'}>review_lead_for_merge_part_1</Text>
            <Text fontSize={'14px'} fontWeight="medium">
              review_lead_for_merge_part_2
            </Text>
            <Tooltip
              title={
                <Box padding={'8px'} width={'100%'}>
                  <Text color="gray2" lineHeight={'16px'} fontSize={12}>
                    what_is_master_record_question
                  </Text>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '12px' }}>
                    <li>
                      <Text color="gray2" lineHeight={'16px'} fontSize={12}>
                        master_record_description_1
                      </Text>
                    </li>
                    <li>
                      <Text color="gray2" lineHeight={'16px'} fontSize={12}>
                        master_record_description_2
                      </Text>
                    </li>
                    <li>
                      <Text color="gray2" lineHeight={'16px'} fontSize={12}>
                        master_record_description_3
                      </Text>
                    </li>
                  </ul>
                </Box>
              }
              width={'450px'}
              cursor="pointer"
              textAlign={'left'}
            >
              <Icon icon={['far', 'circle-info']} fontSize={'20px'} color={colors.gray5} />
            </Tooltip>
          </Flex>
        </Flex>
        <Box marginBottom={'auto'}>
          <CloseButton variant="ghost" icon="times" onClick={onClose} />
        </Box>
      </Flex>

      <Flex height={'100%'} padding={'24px'} gap="7px">
        {/* lead 1 */}
        <MasterLeadBox
          onChange={(leadId: string) =>
            handleChange({ masterLeadId: leadId, mergingLeadId: mergeLeadData.selectedLead?.leadId || '' })
          }
          isSelected={mergeLeadData.incomingLead?.leadId === mergePayload?.master_lead_id}
          leadData={mergeLeadData.incomingLead}
          hasSelectedMaster={Boolean(mergePayload?.master_lead_id)}
        />
        <Tooltip
          title={
            <Text width={'188px'} color="gray2" padding={'8px'} lineHeight={'16px'} fontSize={12}>
              select_one_master_record_description
            </Text>
          }
          width={'188px'}
          cursor="pointer"
        >
          {Boolean(mergePayload?.master_lead_id) ? (
            mergePayload?.master_lead_id === mergeLeadData.incomingLead?.leadId ? (
              <Icon icon={faAngleDoubleLeft} fontSize={'20px'} color={colors.blue1} />
            ) : (
              <Icon icon={faAngleDoubleRight} fontSize={'20px'} color={colors.blue1} />
            )
          ) : (
            <Icon icon={['far', 'circle-question']} fontSize={'20px'} color={colors.gray5} />
          )}
        </Tooltip>
        {/* lead 2 */}
        {mergeLeadData.selectedLead && (
          <MasterLeadBox
            onChange={(leadId: string) =>
              handleChange({ masterLeadId: leadId, mergingLeadId: mergeLeadData.incomingLead?.leadId || '' })
            }
            isSelected={mergeLeadData.selectedLead?.leadId == mergePayload?.master_lead_id}
            leadData={mergeLeadData.selectedLead}
            hasSelectedMaster={Boolean(mergePayload?.master_lead_id)}
          />
        )}
      </Flex>
    </Modal>
  )
}

const CloseButton = styled((props) => <IconButton {...props} />)`
  & svg {
    color: ${(p) => p.theme.colors.gray5};
  }
`
